import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Store, Loader2, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";

const BG = "https://images.unsplash.com/photo-1768734836223-82f8930babee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHw0fHxsb2NhbCUyMHN0cmVldCUyMG1hcmtldCUyMHByb2R1Y2V8ZW58MHx8fHwxNzg2MTk0MTE4fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [master, setMaster] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = master
        ? await api.post("/auth/master-login", { password: form.password })
        : await api.post("/auth/login", form);
      setUser(data);
      toast.success(`Bem-vindo(a), ${data.name}!`);
      navigate("/painel", { replace: true });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="relative hidden lg:block">
        <img src={BG} alt="feira" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/70" />
        <div className="relative z-10 h-full flex flex-col justify-end p-12 text-white">
          <Store className="w-10 h-10 mb-4 text-primary" />
          <h2 className="font-display text-4xl font-black leading-tight">Seu caderno de crediário, agora digital</h2>
          <p className="mt-3 text-white/70 text-lg">Tudo organizado, sem perder conta de quem deve.</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="lg:hidden w-12 h-12 rounded-2xl bg-primary flex items-center justify-center mb-5"><Store className="w-6 h-6 text-primary-foreground" /></div>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-primary">{master ? "Usuário master" : "Entrar"}</h1>
          <p className="text-muted-foreground mt-2 mb-8">{master ? "Digite sua senha master para abrir o sistema." : "Acesse o painel da sua loja."}</p>
          <form onSubmit={submit} className="space-y-5">
            {!master && <div><Label className="text-muted-foreground">E-mail ou telefone</Label><Input data-testid="login-email" className="mt-1.5 rounded-xl bg-card" style={{ height: 52 }} value={form.email} onChange={set("email")} placeholder="voce@email.com" required /></div>}
            <div><Label className="text-muted-foreground">{master ? "Senha master" : "Senha"}</Label><Input data-testid="login-password" type="password" className="mt-1.5 rounded-xl bg-card" style={{ height: 52 }} value={form.password} onChange={set("password")} placeholder={master ? "Senha master" : "Sua senha"} required /></div>
            <Button data-testid="login-submit" disabled={loading} className="w-full rounded-full text-lg font-semibold" style={{ height: 56 }}>{loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Entrar"}</Button>
          </form>
          <button type="button" data-testid="master-toggle" onClick={() => { setMaster(!master); setForm({ ...form, password: "" }); }}
            className="w-full mt-4 inline-flex items-center justify-center gap-2 rounded-full border-2 border-primary/50 text-primary font-semibold hover:bg-primary/10 transition-colors" style={{ height: 48 }}>
            <ShieldCheck className="w-5 h-5" /> {master ? "Voltar ao login normal" : "Entrar como usuário master"}
          </button>
          <Link to="/recuperar-senha" data-testid="forgot-link" className="block text-center mt-6 text-muted-foreground hover:text-primary font-medium transition-colors">Esqueci minha senha</Link>
          <p data-testid="credito-autor" className="mt-10 text-center text-xs font-medium text-primary/70" style={{ textShadow: "0 2px 8px rgba(0,0,0,0.6), 0 0 18px rgba(45,212,191,0.35)" }}>Criado e desenvolvido por Wagner Manhães Miranda</p>
          <a data-testid="contato-link" href="mailto:waguinhodocapao38@gmail.com?subject=Contato%20-%20Caderno%20de%20Credi%C3%A1rio" className="block text-center mt-2 text-xs font-semibold text-primary hover:underline transition-colors" style={{ textShadow: "0 2px 8px rgba(0,0,0,0.6), 0 0 18px rgba(45,212,191,0.35)" }}>Contato</a>
        </motion.div>
      </div>
    </div>
  );
}
